import { supabase } from './supabase';
import { fetchScheduledEmailPreview } from './previewScheduledEmail';
import { mapMapNodeRow, SYSTEM_MAP_STORAGE_BUCKET, type MapAnnotations, type MapNodeRow } from './systemMap';

export interface MapAutomationStep {
  stepId: string;
  position: number;
  name: string | null;
  scheduleLabel: string;
  dayOffset: number;
  timeOfDay: string;
  screenshotUrl: string | null;
  capturedAt: string | null;
}

export interface MapAutomationWorkflow {
  automationId: string;
  automationAlias: string | null;
  /** inline = steps inside parent box; chain = separate step nodes to the right */
  layout?: 'inline' | 'chain';
  steps: MapAutomationStep[];
}

export interface MapAutomationStepRef {
  stepId: string;
  automationId: string;
  parentNodeId: string;
  parentSlug: string;
  position: number;
  scheduleLabel: string;
  dayOffset: number;
  timeOfDay: string;
}

export const AUTOMATION_STEP_NODE_WIDTH = 200;
export const AUTOMATION_STEP_NODE_HEIGHT = 224;
export const AUTOMATION_CHAIN_GAP = 48;
export const AUTOMATION_PARENT_CHAIN_WIDTH = 280;
export const AUTOMATION_PARENT_CHAIN_HEIGHT = 132;

export interface AutomationRowForMap {
  id: string;
  name: string;
  alias: string | null;
  description: string | null;
}

export interface AutomationStepRowForMap {
  id: string;
  automation_id: string;
  position: number;
  day_offset: number;
  time_of_day: string;
  name: string | null;
  subject: string | null;
  body_html: string | null;
  content_mode: 'manual' | 'template_wrap' | 'template_only';
  postmark_template_alias: string | null;
  active: boolean;
}

export function describeAutomationSchedule(dayOffset: number, timeOfDay: string): string {
  const dayPart =
    dayOffset === 0 ? 'Same day' : dayOffset === 1 ? 'Next day' : `Day +${dayOffset}`;
  return `${dayPart} · ${timeOfDay}`;
}

export function automationStepStoragePath(nodeSlug: string, position: number, ext = '.webp'): string {
  return `${nodeSlug}-step-${position}${ext}`;
}

export function automationStepNodeSlug(parentSlug: string, position: number): string {
  return `${parentSlug}-step-${position}`;
}

export function automationParentChainSize(): { width: number; height: number } {
  return { width: AUTOMATION_PARENT_CHAIN_WIDTH, height: AUTOMATION_PARENT_CHAIN_HEIGHT };
}

export function automationStepNodeSeedPosition(
  parentX: number,
  parentY: number,
  parentWidth: number,
  stepIndex: number,
): { x: number; y: number } {
  return {
    x:
      parentX +
      parentWidth +
      AUTOMATION_CHAIN_GAP +
      stepIndex * (AUTOMATION_STEP_NODE_WIDTH + AUTOMATION_CHAIN_GAP),
    y: parentY,
  };
}

export function parseAutomationStepRef(raw: unknown): MapAutomationStepRef | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const stepId = typeof r.stepId === 'string' ? r.stepId : '';
  const parentNodeId = typeof r.parentNodeId === 'string' ? r.parentNodeId : '';
  if (!stepId || !parentNodeId) return undefined;
  return {
    stepId,
    automationId: typeof r.automationId === 'string' ? r.automationId : '',
    parentNodeId,
    parentSlug: typeof r.parentSlug === 'string' ? r.parentSlug : '',
    position: Number(r.position ?? 0),
    scheduleLabel:
      typeof r.scheduleLabel === 'string'
        ? r.scheduleLabel
        : describeAutomationSchedule(Number(r.dayOffset ?? 0), String(r.timeOfDay ?? '08:00')),
    dayOffset: Number(r.dayOffset ?? 0),
    timeOfDay: typeof r.timeOfDay === 'string' ? r.timeOfDay : '08:00',
  };
}

export function automationNodeDefaultSize(stepCount: number): { width: number; height: number } {
  const stepWidth = 136;
  const padding = 40;
  const width = Math.min(960, Math.max(300, stepCount * stepWidth + padding));
  return { width, height: 228 };
}

export function parseAutomationWorkflow(raw: unknown): MapAutomationWorkflow | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const w = raw as Record<string, unknown>;
  const automationId = typeof w.automationId === 'string' ? w.automationId : '';
  if (!automationId) return undefined;

  const stepsRaw = w.steps;
  const steps: MapAutomationStep[] = [];
  if (Array.isArray(stepsRaw)) {
    for (const item of stepsRaw) {
      if (!item || typeof item !== 'object') continue;
      const s = item as Record<string, unknown>;
      const stepId = typeof s.stepId === 'string' ? s.stepId : '';
      const position = Number(s.position);
      if (!stepId || !Number.isFinite(position)) continue;
      steps.push({
        stepId,
        position,
        name: typeof s.name === 'string' ? s.name : null,
        scheduleLabel:
          typeof s.scheduleLabel === 'string'
            ? s.scheduleLabel
            : describeAutomationSchedule(Number(s.dayOffset ?? 0), String(s.timeOfDay ?? '08:00')),
        dayOffset: Number(s.dayOffset ?? 0),
        timeOfDay: typeof s.timeOfDay === 'string' ? s.timeOfDay : '08:00',
        screenshotUrl: typeof s.screenshotUrl === 'string' ? s.screenshotUrl : null,
        capturedAt: typeof s.capturedAt === 'string' ? s.capturedAt : null,
      });
    }
  }

  steps.sort((a, b) => a.position - b.position);

  return {
    automationId,
    automationAlias: typeof w.automationAlias === 'string' ? w.automationAlias : null,
    layout: w.layout === 'chain' || w.layout === 'inline' ? w.layout : undefined,
    steps,
  };
}

export function mergeAutomationStepsPreservingScreenshots(
  existing: MapAutomationStep[],
  incoming: MapAutomationStep[],
): MapAutomationStep[] {
  const byStepId = new Map(existing.map((s) => [s.stepId, s]));
  return incoming.map((step) => {
    const prev = byStepId.get(step.stepId);
    if (!prev?.screenshotUrl) return step;
    return {
      ...step,
      screenshotUrl: prev.screenshotUrl,
      capturedAt: prev.capturedAt,
    };
  });
}

export function automationEngineUrl(automationId: string): string {
  return `https://engine.lowcodecto.com/automations/${automationId}`;
}

export async function listAutomationsForMap(): Promise<AutomationRowForMap[]> {
  const { data, error } = await supabase
    .from('automations')
    .select('id, name, alias, description')
    .order('name');
  if (error) throw error;
  return (data ?? []) as AutomationRowForMap[];
}

export async function fetchAutomationStepsForMap(
  automationId: string,
): Promise<AutomationStepRowForMap[]> {
  const { data, error } = await supabase
    .from('automation_steps')
    .select(
      'id, automation_id, position, day_offset, time_of_day, name, subject, body_html, content_mode, postmark_template_alias, active',
    )
    .eq('automation_id', automationId)
    .eq('active', true)
    .order('position');
  if (error) throw error;
  return (data ?? []) as AutomationStepRowForMap[];
}

export function stepsToMapAutomationSteps(rows: AutomationStepRowForMap[]): MapAutomationStep[] {
  return rows.map((row) => ({
    stepId: row.id,
    position: row.position,
    name: row.name,
    scheduleLabel: describeAutomationSchedule(row.day_offset, row.time_of_day),
    dayOffset: row.day_offset,
    timeOfDay: row.time_of_day,
    screenshotUrl: null,
    capturedAt: null,
  }));
}

export async function syncMapNodeFromAutomation(node: MapNodeRow): Promise<MapNodeRow> {
  const workflow = node.annotations.automation;
  if (!workflow?.automationId) {
    throw new Error('Link an automation before syncing');
  }

  const { data: automation, error: autoErr } = await supabase
    .from('automations')
    .select('id, name, alias, description')
    .eq('id', workflow.automationId)
    .maybeSingle();
  if (autoErr) throw autoErr;
  if (!automation) throw new Error('Automation not found');

  const stepRows = await fetchAutomationStepsForMap(workflow.automationId);
  const incoming = stepsToMapAutomationSteps(stepRows);
  const mergedSteps = mergeAutomationStepsPreservingScreenshots(workflow.steps, incoming);
  const layout = workflow.layout ?? 'inline';
  const size =
    layout === 'chain'
      ? automationParentChainSize()
      : automationNodeDefaultSize(mergedSteps.length);

  const annotations: MapAnnotations = {
    ...node.annotations,
    automation: {
      automationId: automation.id,
      automationAlias: automation.alias,
      layout,
      steps: mergedSteps,
    },
  };

  const { data, error } = await supabase
    .from('map_nodes')
    .update({
      label: automation.name,
      role_description: automation.description,
      url: automationEngineUrl(automation.id),
      width: size.width,
      height: size.height,
      annotations,
    })
    .eq('id', node.id)
    .select('*')
    .single();

  if (error) throw error;
  let parent = mapMapNodeRow(data as Record<string, unknown>);
  if (layout === 'chain') {
    const result = await materializeAutomationStepChain(parent, node.map_id);
    parent = result.parent;
  }
  return parent;
}

export async function linkMapNodeToAutomation(
  node: MapNodeRow,
  automationId: string,
): Promise<MapNodeRow> {
  const { data: automation, error: autoErr } = await supabase
    .from('automations')
    .select('id, name, alias, description')
    .eq('id', automationId)
    .maybeSingle();
  if (autoErr) throw autoErr;
  if (!automation) throw new Error('Automation not found');

  const stepRows = await fetchAutomationStepsForMap(automationId);
  const steps = stepsToMapAutomationSteps(stepRows);
  const size = automationNodeDefaultSize(steps.length);

  const annotations: MapAnnotations = {
    ...node.annotations,
    automation: {
      automationId: automation.id,
      automationAlias: automation.alias,
      steps,
    },
  };

  const { data, error } = await supabase
    .from('map_nodes')
    .update({
      kind: 'automation',
      label: automation.name,
      role_description: automation.description,
      url: automationEngineUrl(automation.id),
      width: size.width,
      height: size.height,
      annotations,
    })
    .eq('id', node.id)
    .select('*')
    .single();

  if (error) throw error;
  return mapMapNodeRow(data as Record<string, unknown>);
}

function screenshotExtForFile(file: File): string {
  if (file.type === 'image/png') return '.png';
  if (file.type === 'image/webp') return '.webp';
  if (file.type === 'image/jpeg' || file.type === 'image/jpg') return '.jpg';
  const name = file.name.toLowerCase();
  if (name.endsWith('.png')) return '.png';
  if (name.endsWith('.webp')) return '.webp';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return '.jpg';
  return '.webp';
}

export async function captureAutomationStepPreviewScreenshot(
  node: MapNodeRow,
  stepId: string,
  stepRow: AutomationStepRowForMap,
): Promise<MapNodeRow> {
  const preview = await fetchScheduledEmailPreview({
    subject: stepRow.subject,
    bodyHtml: stepRow.body_html,
    postmarkTemplateAlias: stepRow.postmark_template_alias,
    contentMode: stepRow.content_mode,
  });

  const blob = await htmlDocumentToWebpBlob(preview.html);
  const file = new File([blob], `step-${stepRow.position}.webp`, { type: 'image/webp' });
  return uploadAutomationStepScreenshot(node, stepId, file);
}

async function htmlDocumentToWebpBlob(html: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.style.cssText =
      'position:fixed;left:-10000px;top:0;width:640px;height:1200px;border:0;visibility:hidden';
    document.body.appendChild(iframe);

    const cleanup = () => {
      iframe.remove();
    };

    iframe.onload = () => {
      window.setTimeout(() => {
        try {
          const doc = iframe.contentDocument;
          if (!doc?.body) {
            cleanup();
            reject(new Error('Preview iframe has no body'));
            return;
          }

          const target = doc.documentElement;
          const width = Math.min(640, target.scrollWidth || 640);
          const height = Math.min(1200, target.scrollHeight || 800);

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            cleanup();
            reject(new Error('Canvas not supported'));
            return;
          }

          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);

          const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
<foreignObject width="100%" height="100%">
<div xmlns="http://www.w3.org/1999/xhtml">${target.outerHTML}</div>
</foreignObject></svg>`;

          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, 0, 0);
            canvas.toBlob(
              (blob) => {
                cleanup();
                if (blob) resolve(blob);
                else reject(new Error('Failed to encode preview image'));
              },
              'image/webp',
              0.92,
            );
          };
          img.onerror = () => {
            cleanup();
            reject(new Error('Failed to rasterize preview HTML'));
          };
          img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
        } catch (e) {
          cleanup();
          reject(e instanceof Error ? e : new Error(String(e)));
        }
      }, 600);
    };

    iframe.onerror = () => {
      cleanup();
      reject(new Error('Failed to load preview iframe'));
    };

    iframe.srcdoc = html;
  });
}

function chainEdgeMetadata(parentSlug: string) {
  return { automationChain: true, automationParentSlug: parentSlug };
}

export async function materializeAutomationStepChain(
  parentNode: MapNodeRow,
  mapId: string,
): Promise<{ parent: MapNodeRow; stepNodes: MapNodeRow[] }> {
  const workflow = parentNode.annotations.automation;
  if (!workflow?.automationId) throw new Error('Parent has no linked automation');

  const steps = [...workflow.steps].sort((a, b) => a.position - b.position);
  const chainSize = automationParentChainSize();

  const parentAnnotations: MapAnnotations = {
    ...parentNode.annotations,
    automation: {
      ...workflow,
      layout: 'chain',
      steps,
    },
  };

  const { data: parentData, error: parentErr } = await supabase
    .from('map_nodes')
    .update({
      width: chainSize.width,
      height: chainSize.height,
      annotations: parentAnnotations,
    })
    .eq('id', parentNode.id)
    .select('*')
    .single();
  if (parentErr) throw parentErr;
  const parent = mapMapNodeRow(parentData as Record<string, unknown>);

  const { data: existingStepNodes, error: existingErr } = await supabase
    .from('map_nodes')
    .select('*')
    .eq('map_id', mapId)
    .like('slug', `${parent.slug}-step-%`);
  if (existingErr) throw existingErr;

  const existingBySlug = new Map(
    (existingStepNodes ?? []).map((row) => [String(row.slug).toLowerCase(), mapMapNodeRow(row as Record<string, unknown>)]),
  );
  const activeSlugs = new Set<string>();

  const stepNodes: MapNodeRow[] = [];

  for (let index = 0; index < steps.length; index += 1) {
    const step = steps[index];
    const slug = automationStepNodeSlug(parent.slug, step.position);
    activeSlugs.add(slug.toLowerCase());
    const existing = existingBySlug.get(slug.toLowerCase());
    const seed = automationStepNodeSeedPosition(parent.x, parent.y, chainSize.width, index);
    const stepLabel = step.name?.trim() || `Step ${step.position}`;
    const stepAnnotations: MapAnnotations = {
      automationStep: {
        stepId: step.stepId,
        automationId: workflow.automationId,
        parentNodeId: parent.id,
        parentSlug: parent.slug,
        position: step.position,
        scheduleLabel: step.scheduleLabel,
        dayOffset: step.dayOffset,
        timeOfDay: step.timeOfDay,
      },
    };

    const payload = {
      map_id: mapId,
      slug,
      label: stepLabel,
      kind: 'automation_step' as const,
      layer: parent.layer,
      role_description: step.scheduleLabel,
      usage_notes: `Email ${step.position} in ${parent.label}. Preview from Postmark template at send time.`,
      url: automationEngineUrl(workflow.automationId),
      repo_path: 'engine-ui/src/components/Automations/',
      width: AUTOMATION_STEP_NODE_WIDTH,
      height: AUTOMATION_STEP_NODE_HEIGHT,
      show_thumbnail: Boolean(step.screenshotUrl),
      screenshot_url: step.screenshotUrl,
      captured_at: step.capturedAt,
      annotations: stepAnnotations,
    };

    let stepNode: MapNodeRow;
    if (existing) {
      const { data, error } = await supabase
        .from('map_nodes')
        .update({
          label: payload.label,
          kind: payload.kind,
          role_description: payload.role_description,
          usage_notes: payload.usage_notes,
          url: payload.url,
          width: payload.width,
          height: payload.height,
          show_thumbnail: payload.show_thumbnail,
          screenshot_url: payload.screenshot_url,
          captured_at: payload.captured_at,
          annotations: payload.annotations,
        })
        .eq('id', existing.id)
        .select('*')
        .single();
      if (error) throw error;
      stepNode = mapMapNodeRow(data as Record<string, unknown>);
    } else {
      const { data, error } = await supabase
        .from('map_nodes')
        .insert({ ...payload, x: seed.x, y: seed.y })
        .select('*')
        .single();
      if (error) throw error;
      stepNode = mapMapNodeRow(data as Record<string, unknown>);
    }

    stepNodes.push(stepNode);
  }

  const { data: oldChainEdges, error: oldChainErr } = await supabase
    .from('map_edges')
    .select('id')
    .eq('map_id', mapId)
    .contains('metadata', chainEdgeMetadata(parent.slug));
  if (oldChainErr) throw oldChainErr;
  if (oldChainEdges?.length) {
    const { error: deleteChainErr } = await supabase
      .from('map_edges')
      .delete()
      .in(
        'id',
        oldChainEdges.map((edge) => edge.id),
      );
    if (deleteChainErr) throw deleteChainErr;
  }

  let previousNodeId = parent.id;
  for (const stepNode of stepNodes) {
    const step = steps.find((s) => automationStepNodeSlug(parent.slug, s.position) === stepNode.slug);
    const { error: insertEdgeErr } = await supabase.from('map_edges').insert({
      map_id: mapId,
      source_node_id: previousNodeId,
      target_node_id: stepNode.id,
      label: step?.scheduleLabel ?? null,
      edge_type: 'data',
      metadata: chainEdgeMetadata(parent.slug),
    });
    if (insertEdgeErr) throw insertEdgeErr;
    previousNodeId = stepNode.id;
  }

  for (const row of existingStepNodes ?? []) {
    const slug = String(row.slug).toLowerCase();
    if (activeSlugs.has(slug)) continue;
    const nodeId = String(row.id);
    await supabase.from('map_edges').delete().eq('map_id', mapId).or(
      `source_node_id.eq.${nodeId},target_node_id.eq.${nodeId}`,
    );
    await supabase.from('map_nodes').delete().eq('id', nodeId);
  }

  return { parent, stepNodes };
}

async function applyStepScreenshotToChainNodes(
  parentNode: MapNodeRow,
  mapId: string,
  stepId: string,
  screenshotUrl: string,
  capturedAt: string,
): Promise<void> {
  const workflow = parentNode.annotations.automation;
  const step = workflow?.steps.find((s) => s.stepId === stepId);
  if (!step) return;

  const slug = automationStepNodeSlug(parentNode.slug, step.position);
  await supabase
    .from('map_nodes')
    .update({
      show_thumbnail: true,
      screenshot_url: screenshotUrl,
      captured_at: capturedAt,
    })
    .eq('map_id', mapId)
    .eq('slug', slug);
}

export async function uploadAutomationStepScreenshot(
  node: MapNodeRow,
  stepId: string,
  file: File,
): Promise<MapNodeRow> {
  const workflow = node.annotations.automation;
  if (!workflow) throw new Error('Not an automation workflow node');

  const step = workflow.steps.find((s) => s.stepId === stepId);
  if (!step) throw new Error('Step not found on this node');

  const ext = screenshotExtForFile(file);
  const storagePath = automationStepStoragePath(node.slug, step.position, ext);
  const contentType =
    file.type && file.type.startsWith('image/')
      ? file.type
      : ext === '.png'
        ? 'image/png'
        : ext === '.webp'
          ? 'image/webp'
          : 'image/jpeg';

  const { error: uploadError } = await supabase.storage
    .from(SYSTEM_MAP_STORAGE_BUCKET)
    .upload(storagePath, file, {
      contentType,
      upsert: true,
      cacheControl: '3600',
    });
  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage.from(SYSTEM_MAP_STORAGE_BUCKET).getPublicUrl(storagePath);
  const capturedAt = new Date().toISOString();

  const nextSteps = workflow.steps.map((s) =>
    s.stepId === stepId
      ? { ...s, screenshotUrl: urlData.publicUrl, capturedAt }
      : s,
  );

  const annotations: MapAnnotations = {
    ...node.annotations,
    automation: { ...workflow, steps: nextSteps },
  };

  const { data, error } = await supabase
    .from('map_nodes')
    .update({ annotations })
    .eq('id', node.id)
    .select('*')
    .single();

  if (error) throw error;
  const updated = mapMapNodeRow(data as Record<string, unknown>);

  if (workflow.layout === 'chain') {
    await applyStepScreenshotToChainNodes(
      updated,
      node.map_id,
      stepId,
      urlData.publicUrl,
      capturedAt,
    );
  }

  return updated;
}

export async function captureAllAutomationStepPreviews(node: MapNodeRow): Promise<MapNodeRow> {
  const workflow = node.annotations.automation;
  if (!workflow?.automationId) throw new Error('Link an automation first');

  const stepRows = await fetchAutomationStepsForMap(workflow.automationId);
  let current = node;
  for (const stepRow of stepRows) {
    current = await captureAutomationStepPreviewScreenshot(current, stepRow.id, stepRow);
  }

  if (workflow.layout === 'chain') {
    await materializeAutomationStepChain(current, node.map_id);
    const { data, error } = await supabase.from('map_nodes').select('*').eq('id', node.id).single();
    if (error) throw error;
    return mapMapNodeRow(data as Record<string, unknown>);
  }

  return current;
}
